import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Plus, Receipt } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { CardCreateDrawer, CardTxRecordDrawer } from '../../components/CardActionDrawers';
import { useCards, useCardTransactions } from '../../hooks/useCards';
import { formatAmount, formatDate } from '../../lib/format';

export function CardsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const cards = useCards(wsId);
  const txs = useCardTransactions(wsId);
  const [addOpen, setAddOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="display text-2xl font-semibold">{t('cards.title')}</h1>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setRecordOpen(true)}
              disabled={!cards.data?.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium disabled:opacity-40 disabled:pointer-events-none"
            >
              <Receipt size={14} />
              {t('cards.recordTransaction')}
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
            >
              <Plus size={14} />
              {t('cards.addCard')}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {(cards.data ?? []).map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-accent-50 dark:bg-accent-900/40 text-accent-700 flex items-center justify-center">
                  <CreditCard size={18} />
                </div>
                <div>
                  <div className="font-medium uppercase">
                    {c.brand ?? 'Card'} •••• {c.last4 ?? '0000'}
                  </div>
                  <div className="text-xs text-ink-500">
                    {c.provider} · {c.currency}
                  </div>
                </div>
              </div>
              {c.limit_cents && (
                <div className="mt-3 text-xs text-ink-500">
                  {t('common.limit')}:{' '}
                  <span className="num">{formatAmount(c.limit_cents, c.currency, i18n.language)}</span>
                </div>
              )}
            </Card>
          ))}
          {cards.data?.length === 0 && (
            <Card className="p-10 text-center text-ink-500 col-span-3">{t('cards.noCards')}</Card>
          )}
        </div>

        <Card>
          <div className="px-5 py-4 border-b border-ink-200 dark:border-ink-800 font-medium">
            {t('cards.recentTransactions')}
          </div>
          {txs.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : txs.data?.length === 0 ? (
            <div className="p-10 text-center text-ink-500">{t('cards.noTransactions')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {(txs.data ?? []).map((tx) => (
                <li key={tx.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{tx.merchant ?? '—'}</div>
                    <div className="text-xs text-ink-500">
                      {formatDate(tx.occurred_at, i18n.language)} · {tx.mcc ?? ''} · {tx.status}
                    </div>
                  </div>
                  <span className="num font-medium">
                    {formatAmount(tx.amount_cents, tx.currency, i18n.language)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <CardCreateDrawer wsId={wsId} open={addOpen} onOpenChange={setAddOpen} />
          <CardTxRecordDrawer wsId={wsId} open={recordOpen} onOpenChange={setRecordOpen} />
        </>
      )}
    </AppShell>
  );
}
